import startCase from 'lodash/startCase';
import uniq from 'lodash/uniq';

import type {
  CodeDestination,
  GatedCall,
  SignInAttempt,
  SignInChannel,
} from '@/lib/guest-auth/types';

interface SignInMethodEntry {
  succeeded: readonly string[];
  /** Each labelled under its own key in `approvalCard.signInHistory.attempts`, which the back
   *  office reads too. */
  problems: readonly string[];
}

/** Results are matched without their method (the conversation record keeps only the result), so no
 *  two methods may share a result name. */
export const SIGN_IN_METHODS = {
  otp: {
    succeeded: ['sent', 'verified'],
    problems: ['wrongCode', 'expired', 'tooMany', 'sendFailed', 'error'],
  },
  website: {
    succeeded: ['signedIn'],
    problems: ['unavailable', 'rejected', 'sessionExpired'],
  },
} as const satisfies Record<string, SignInMethodEntry>;

export type SignInMethod = keyof typeof SIGN_IN_METHODS;

export type SignInProblem = (typeof SIGN_IN_METHODS)[SignInMethod]['problems'][number];

export type SignInResultOf<M extends SignInMethod> =
  | (typeof SIGN_IN_METHODS)[M]['succeeded'][number]
  | (typeof SIGN_IN_METHODS)[M]['problems'][number];

function problemsOf(method: SignInMethod): SignInMethodEntry['problems'] {
  return SIGN_IN_METHODS[method].problems;
}

/** Named one by one: nothing else the form holds (the ID, the code) may reach analytics.
 *  PostHog types a property name once per project, and other events send numeric `step` and
 *  `result`, so these strings would read back as null under the bare names. */
export function signInAttemptProperties({
  method,
  step,
  result,
  toolName,
  toolCallId,
  channel,
  deliverTo,
}: SignInAttempt & Partial<GatedCall> & { channel: SignInChannel; deliverTo?: CodeDestination }) {
  return {
    method,
    signInStep: step,
    signInResult: result,
    problem: problemsOf(method).includes(result),
    channel,
    ...(deliverTo && { deliverTo }),
    ...(toolName && { toolName }),
    ...(toolCallId && { toolCallId }),
  };
}

export function signInProblemsOf(methods: readonly SignInMethod[]): string[] {
  return uniq(methods.flatMap(problemsOf));
}

const KNOWN_SIGN_IN_PROBLEMS: ReadonlySet<string> = new Set(
  signInProblemsOf(Object.keys(SIGN_IN_METHODS) as SignInMethod[]),
);

export function isKnownSignInProblem(result: string): result is SignInProblem {
  return KNOWN_SIGN_IN_PROBLEMS.has(result);
}

interface ProblemLabels {
  (key: string): string;
  has: (key: string) => boolean;
}

/** A problem no method names, or one an app has no label for yet, reads as its own name; one
 *  recorded without a result reads as a sign-in problem, never as a blank chip. */
export function signInProblemLabel({ result, t }: { result: string; t: ProblemLabels }): string {
  if (!result) return t('unknown');
  return isKnownSignInProblem(result) && t.has(result) ? t(result) : startCase(result);
}
